import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { StorageAPI } from '@makyoapp/makyo-sdk'
import { FileCategory } from '@makyoapp/makyo-sdk/models/Files'
import { Tabs } from '@makyoapp/makyo-ui'

import { queryKeys } from '@/lib/react-query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { FileManagerKind, useFileManagerContext } from '@/context/FileManagerContext'
import { useReqError } from '@/hooks/useReqError'
import { sanitizeFileName } from '@/shared/utils'

import { UploadField } from '../UploadField'

const defaultFileTypes: FileCategory[] = [
  FileCategory.Image,
  FileCategory.Audio,
  FileCategory.Video,
  FileCategory.Document,
  FileCategory.Other,
  FileCategory.Folder,
]

const fileExtensionMap = {
  [FileCategory.Image]: ['image/*'],
  [FileCategory.Video]: ['video/*'],
  [FileCategory.Audio]: ['audio/*'],
  [FileCategory.Document]: [
    '.doc',
    '.docx',
    'application/msword',
    '.xls',
    '.xlsx',
    '.csv',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    '.pdf',
    '.html',
    '.ppt',
    '.pptx',
  ],
  [FileCategory.Other]: ['.js', '.css'],
  [FileCategory.Folder]: [],
}

function getSortedAcceptedFileTypes(fileTypes: FileCategory[]): string[] {
  const acceptedFileTypes: string[] = []

  fileTypes.forEach((fileType) => {
    const extensions = fileExtensionMap[fileType]
    if (extensions) {
      acceptedFileTypes.push(...extensions)
    }
  })

  return acceptedFileTypes
}

function isFileTypeAccepted(file: File, acceptedFileTypes: FileCategory[]): boolean {
  const acceptedExtensions = acceptedFileTypes.flatMap((fileType) => fileExtensionMap[fileType])
  const fileExtension = file.name.split('.').pop()?.toLowerCase()
  const fileMimeType = file.type.toLowerCase()

  const isExtensionAccepted = fileExtension && acceptedExtensions.includes(`.${fileExtension}`)

  const isMimeTypeAccepted = acceptedExtensions.some((mimeType) => {
    if (mimeType.endsWith('/*')) {
      const wildcard = mimeType.slice(0, -1)
      return fileMimeType.startsWith(wildcard)
    }
    return fileMimeType === mimeType
  })

  return !!isExtensionAccepted || isMimeTypeAccepted
}

/**
 * Upload Panel for File Manager
 */
export function UploadPanel() {
  const printAPIError = useReqError()
  const { onChange, onHide, fileTypes } = useFileManagerContext()
  const { currentOrg } = useCurrentOrgContext()
  const queryClient = useQueryClient()

  const [error, setError] = useState('')

  const validFileTypes = fileTypes ?? defaultFileTypes
  const acceptedFileTypes = getSortedAcceptedFileTypes(validFileTypes)

  return (
    <Tabs.Panel data-panel-id={FileManagerKind.Upload} eventKey={FileManagerKind.Upload}>
      <UploadField
        className='mk-fm-upload-input'
        accept={acceptedFileTypes.join(',')}
        inputProps={{
          withThumb: false,
          error,
        }}
        onSubmit={(value, setIsLoading, done) => {
          if (value.file && currentOrg) {
            setIsLoading(true)
            const name = sanitizeFileName(value.file.name)
            StorageAPI.ReqUploadForOrg(name, currentOrg.id, value.file, 0 as unknown as AbortSignal)
              .then((res) => {
                queryClient.invalidateQueries({
                  queryKey: [queryKeys.orgStorage, currentOrg.id],
                })
                onChange(FileManagerKind.Upload, res._File._Link)
                onHide?.()
              })
              .catch(printAPIError)
              .finally(() => {
                done()
                setIsLoading(false)
              })
          }
        }}
        validate={(file) => {
          if (!isFileTypeAccepted(file, validFileTypes)) {
            setError('The selected file type is not allowed, please try with another file.')
            return false
          } else {
            setError('')
            return true
          }
        }}
      />
    </Tabs.Panel>
  )
}
